import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './your-contracts-section.component.scss';

interface Agreement {
  handleTenderAgreement: () => void;
  handleContractAgreement: () => void;
  // Render only one card: 'customer' (above the agreements list) or 'tender'
  // (standalone). Omit to render both with the section header (original layout).
  variant?: 'customer' | 'tender';
  onViewAgreements?: () => void;
}

/* ---- Inline lucide-style icons (repo has no lucide-react dependency) ---- */
type IconProps = { size?: number; className?: string };
const stroke = {
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const ClockIcon = ({ size = 14, className }: IconProps) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const PenLineIcon = ({ size = 20, className }: IconProps) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z" />
  </svg>
);

const FileSignatureIcon = ({ size = 20, className }: IconProps) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <path d="M20 19.5v.5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h7.5L20 8.5" />
    <path d="M8 18h1" />
    <path d="M18.42 9.61a2.1 2.1 0 1 1 2.97 2.97L16.95 17 13 18l.99-3.95z" />
  </svg>
);

const ArrowRightIcon = ({ size = 16, className }: IconProps) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const ChevronDownIcon = ({ size = 16, className }: IconProps) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const CheckCircle2Icon = ({ size = 16, className }: IconProps) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <path d="M21.801 10A10 10 0 1 1 17 3.335" />
    <path d="m9 11 3 3L22 4" />
  </svg>
);

const YourContractsSkeleton = ({ single = false }: { single?: boolean }) => (
  <div className="your-contracts-section your-contracts-section--loading" aria-hidden="true">
    {!single && <div className="yc-skel yc-skel--title" />}
    <div className={`your-contracts-section__options-container${single ? ' your-contracts-section__options-container--single' : ''}`}>
      <div className="yc-skel yc-skel--card" />
      {!single && <div className="yc-skel yc-skel--card" />}
    </div>
  </div>
);

export const YourContractsSection = ({ handleTenderAgreement, handleContractAgreement, variant }: Agreement) => {
  const { t } = useTranslation();
  const showCustomer = variant !== 'tender';
  const showTender = variant !== 'customer';
  const [expandedProcess, setExpandedProcess] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const tenderSteps = [
    t('TENDER_STEP_1') || 'Fill in the quotation fields with what you and your customer agreed.',
    t('TENDER_STEP_2') || 'Print or save it and ask your customer to sign.',
    t('TENDER_STEP_3') || 'Email it to support@invozio.com so we can sign and send a copy to your customer.',
  ];

  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const id = setTimeout(() => setLoading(false), 900);
    return () => clearTimeout(id);
  }, []);

  if (loading) return <YourContractsSkeleton single={!!variant} />;

  return (
    <div className="your-contracts-section ci-reveal" style={{ animationDelay: '400ms' }}>
      {!variant && (
        <div className="your-contracts-section__header">
          <div className="your-contracts-section__heading">
            <h2 className="your-contracts-section__title">
              {t('YOUR_CONTRACTS') || 'Your Contracts'}
            </h2>
            <p className="your-contracts-section__subtitle">
              {t('CONTRACT_OPTIONS_DESCRIPTION') || 'Choose the option that best fits your working arrangement.'}
            </p>
          </div>
          <span className="your-contracts-section__time-pill">
            <ClockIcon size={14} />
            {t('TAKES_TWO_MINUTES', { defaultValue: 'Takes 2 minutes' })}
          </span>
        </div>
      )}

      <div className={`your-contracts-section__options-container${variant ? ' your-contracts-section__options-container--single' : ''}`}>
        {showCustomer && (
        <div className="your-contracts-section__option-card service-contract">
          <div className="your-contracts-section__option-header">
            <span className="your-contracts-section__icon-box">
              <PenLineIcon size={20} />
            </span>
            <h3 className="your-contracts-section__option-title">
              {t('SERVICE_CONTRACT') || 'Customer Agreement'}
            </h3>
          </div>
          <div className="your-contracts
          -section__option-body">
            <p className="your-contracts-section__option-description">
              {t('SERVICE_CONTRACT_DESCRIPTION') || 'Create a customer agreement between your customer and Invozio that defines scope, period, compensation, and key conditions.'}
            </p>
            <div className="your-contracts-section__chips">
              <span className="your-contracts-section__chip">
                {t('SERVICE_CONTRACT_CHIP_SCOPE', { defaultValue: 'Scope & period' })}
              </span>
              <span className="your-contracts-section__chip">
                {t('SERVICE_CONTRACT_CHIP_COMPENSATION', { defaultValue: 'Compensation' })}
              </span>
              <span className="your-contracts-section__chip">
                {t('SERVICE_CONTRACT_CHIP_CONDITIONS', { defaultValue: 'Key conditions' })}
              </span>
            </div>
          </div>
          <div className="your-contracts-section__option-action">
            <button
              className="your-contracts-section__write-contract-btn"
              onClick={() => handleContractAgreement()}
            >
              {t('WRITE_A_CONTRACT') || 'Write a Customer Agreement'}
              <ArrowRightIcon size={16} />
            </button>
          </div>
        </div>
        )}

        {showTender && (
        <div className="your-contracts-section__option-card ci-reveal" style={{ animationDelay: '600ms' }}>
          <span className="your-contracts-section__icon-box your-contracts-section__icon-box--alt">
            <FileSignatureIcon size={20} />
          </span>

          <h3 className="your-contracts-section__option-title">
            {t('TENDER_QUOTATION') || 'Customer Offer'}
          </h3>
          <p className="your-contracts-section__option-description">
            {t('TENDER_QUOTATION_DESCRIPTION') || 'Send an offer your customer can sign. A signed quotation works as a simple contract and can help confirm scheduled hours when needed.'}
          </p>

          <div className="your-contracts-section__option-action">
            <button
              className="your-contracts-section__create-tender-btn"
              onClick={() => handleTenderAgreement()}
            >
              {t('CREATE_A_TENDER') || 'Create a Customer Offer'}
              <ArrowRightIcon size={16} />
            </button>
          </div>

          {/* "How it works" dropdown is hidden for now (kept in code to re-enable later). */}
          {false && (
          <div className="your-contracts-section__how-it-works-wrapper">
            <button
              className="your-contracts-section__how-it-works-btn"
              onClick={() => setExpandedProcess(!expandedProcess)}
              aria-expanded={expandedProcess}
              aria-controls="tender-how-it-works"
            >
              <span>{t('HOW_IT_WORKS') || 'How it works'}</span>
            <svg
                className={`your-contracts-section__chevron${expandedProcess ? ' your-contracts-section__chevron--open' : ''}`}
                xmlns="http://www.w3.org/2000/svg"
                width={11}
                height={6}
                viewBox="0 0 11 6"
                fill="none"
              >
                <path
                  d="M9.83496 0.834961L5.86529 4.80463C5.5724 5.09752 5.09752 5.09752 4.80463 4.80463L0.834961 0.834961"
                  stroke="#7c3aed"
                  strokeWidth="1.67"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            {/* Inline stepper: grid-rows 0fr -> 1fr gives a jank-free height animation */}
            <div
              id="tender-how-it-works"
              className={`your-contracts-section__stepper${expandedProcess ? ' is-open' : ''}`}
            >
              <div className="your-contracts-section__stepper-inner">
                <div className="your-contracts-section__process-card">
                  <h3 className="your-contracts-section__process-title">
                    {t('HOW_TENDER_PROCESS_WORKS') || 'How the Tender Process Works'}
                  </h3>

                  {/* Progress track */}
                  <div
                    className="your-contracts-section__stepper-track"
                    role="tablist"
                    aria-label={t('HOW_TENDER_PROCESS_WORKS') || 'How the Tender Process Works'}
                  >
                    {tenderSteps.map((_, i) => (
                      <Fragment key={i}>
                        <button
                          type="button"
                          className={
                            'your-contracts-section__stepper-dot' +
                            (i === activeStep ? ' is-active' : '') +
                            (i < activeStep ? ' is-done' : '')
                          }
                          onClick={() => setActiveStep(i)}
                          role="tab"
                          aria-selected={i === activeStep}
                          aria-label={`${t('STEP', { defaultValue: 'Step' })} ${i + 1}`}
                        >
                          {i < activeStep ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M20 6 9 17l-5-5" />
                            </svg>
                          ) : (
                            i + 1
                          )}
                        </button>
                        {i < tenderSteps.length - 1 && (
                          <span
                            className={`your-contracts-section__stepper-line${i < activeStep ? ' is-done' : ''}`}
                            aria-hidden="true"
                          />
                        )}
                      </Fragment>
                    ))}
                  </div>

                  {/* Active step */}
                  <div className="your-contracts-section__stepper-panel" key={activeStep}>
                    <span className="your-contracts-section__stepper-badge">
                      {t('STEP', { defaultValue: 'Step' })} {activeStep + 1} / {tenderSteps.length}
                    </span>
                    <p className="your-contracts-section__stepper-text">
                      {tenderSteps[activeStep]}
                    </p>
                  </div>

                  {/* Navigation */}
                  <div className="your-contracts-section__stepper-nav">
                    <button
                      type="button"
                      className="your-contracts-section__stepper-btn your-contracts-section__stepper-btn--ghost"
                      onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
                      disabled={activeStep === 0}
                    >
                      {t('BACK', { defaultValue: 'Back' })}
                    </button>

                    {activeStep < tenderSteps.length - 1 ? (
                      <button
                        type="button"
                        className="your-contracts-section__stepper-btn your-contracts-section__stepper-btn--primary"
                        onClick={() => setActiveStep((s) => Math.min(tenderSteps.length - 1, s + 1))}
                      >
                        {t('NEXT', { defaultValue: 'Next' })}
                        <ArrowRightIcon size={15} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="your-contracts-section__stepper-btn your-contracts-section__stepper-btn--primary"
                        onClick={() => { setExpandedProcess(false); setActiveStep(0); }}
                      >
                        {t('GOT_IT', { defaultValue: 'Got it' })}
                      </button>
                    )}
                  </div>

                  <div className="your-contracts-section__process-footer">
                    <p className="your-contracts-section__footer-text">
                      {t('NEED_HELP') || 'Need help?'} {t('PHONE') || 'Phone:'} <strong>010-494 77 11</strong> ({t('WEEKDAYS_TIME') || 'weekdays 07:00–18:00'}) · {t('EMAIL') || 'Email:'} <strong>support@invozio.com</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};
